import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Stack,
  IconButton
} from '@mui/material';
import {
  Close as CloseIcon,
  Payment as PaymentIcon,
  Launch as LaunchIcon,
  ContentCopy as CopyIcon,
  Share as ShareIcon
} from '@mui/icons-material';
import tripayService from '../services/tripayService';
import { PaymentMethod, PaymentCreationInput, TripayPayment } from '../types/tripay';

interface PaymentDialogProps {
  open: boolean;
  onClose: () => void;
  billId: string;
  billNumber: string;
  totalAmount: number;
  customerName?: string;
  customerEmail?: string;
  customerPhone?: string;
  onPaymentCreated?: (payment: TripayPayment) => void;
}

const PaymentDialog: React.FC<PaymentDialogProps> = ({
  open,
  onClose,
  billId,
  billNumber,
  totalAmount,
  customerName = '',
  customerEmail = '',
  customerPhone = '',
  onPaymentCreated
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [createdPayment, setCreatedPayment] = useState<TripayPayment | null>(null);
  const [step, setStep] = useState<'form' | 'result'>('form');

  const [formData, setFormData] = useState({
    payment_method: '',
    customer_name: '',
    customer_email: '',
    customer_phone: ''
  });

  // Initialize form data with customer info
  useEffect(() => {
    if (open) {
      setFormData({
        payment_method: '',
        customer_name: customerName,
        customer_email: customerEmail,
        customer_phone: customerPhone
      });
      setStep('form');
      setError(null);
      setCreatedPayment(null);
    }
  }, [open, customerName, customerEmail, customerPhone]);

  // Fetch payment methods
  useEffect(() => {
    const fetchPaymentMethods = async () => {
      try {
        const response = await tripayService.payment.getPaymentMethods();
        if (response.success && response.data) {
          setPaymentMethods(response.data);
        } else {
          setError('Gagal memuat metode pembayaran');
        }
      } catch (err) {
        console.error('Error fetching payment methods:', err);
        setError('Gagal memuat metode pembayaran');
      }
    };

    if (open) {
      fetchPaymentMethods();
    }
  }, [open]);

  const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleSubmit = async () => {
    if (!formData.payment_method) {
      setError('Silakan pilih metode pembayaran');
      return;
    }

    if (!formData.customer_name || !formData.customer_email) {
      setError('Nama dan email customer harus diisi');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const selectedMethod = paymentMethods.find(m => m.code === formData.payment_method);
      
      const paymentData: PaymentCreationInput = {
        bill_id: billId,
        payment_method: formData.payment_method,
        customer_name: formData.customer_name,
        customer_email: formData.customer_email,
        customer_phone: formData.customer_phone
      };

      const response = await tripayService.payment.createPayment(paymentData);
      if (response.success && response.data) {
        setCreatedPayment(response.data);
        setStep('result');
        
        if (onPaymentCreated) {
          onPaymentCreated(response.data);
        }
      } else {
        setError(response.message || 'Gagal membuat pembayaran');
      }
    } catch (err: any) {
      console.error('Error creating payment:', err);
      setError(err.response?.data?.message || 'Gagal membuat pembayaran');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFormData({
      payment_method: '',
      customer_name: customerName,
      customer_email: customerEmail,
      customer_phone: customerPhone
    });
    setStep('form');
    setError(null);
    setCreatedPayment(null);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR'
    }).format(amount);
  };

  const handleCopyPaymentUrl = () => {
    if (createdPayment?.checkout_url) {
      navigator.clipboard.writeText(createdPayment.checkout_url);
    }
  };

  const handleSharePayment = () => {
    if (createdPayment?.reference && 'share' in navigator) {
      const publicUrl = `${window.location.origin}/payment/${createdPayment.reference}`;
      navigator.share({
        title: `Pembayaran ${billNumber}`,
        text: `Silakan lakukan pembayaran untuk tagihan ${billNumber}`,
        url: publicUrl
      });
    }
  };

  const renderFormStep = () => (
    <>
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Buat Pembayaran untuk Tagihan {billNumber}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Total: {formatCurrency(totalAmount)}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormControl fullWidth required>
            <InputLabel>Metode Pembayaran</InputLabel>
            <Select
              value={formData.payment_method}
              onChange={(e) => setFormData(prev => ({ ...prev, payment_method: e.target.value }))}
              label="Metode Pembayaran"
            >
              {paymentMethods.map((method) => (
                <MenuItem key={method.code} value={method.code}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <img
                      src={method.icon_url}
                      alt={method.name}
                      style={{ width: 24, height: 24 }}
                    />
                    {method.name}
                    {method.fee_merchant.flat && (
                      <Chip
                        size="small"
                        label={`+${formatCurrency(method.fee_merchant.flat)}`}
                        color="warning"
                        variant="outlined"
                      />
                    )}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            required
            label="Customer Name"
            value={formData.customer_name}
            onChange={handleInputChange('customer_name')}
            InputProps={{
              readOnly: Boolean(customerName),
            }}
            sx={{
              '& .MuiInputBase-input': {
                backgroundColor: customerName ? 'rgba(0, 0, 0, 0.04)' : 'transparent'
              }
            }}
          />

          <TextField
            fullWidth
            required
            type="email"
            label="Customer Email"
            value={formData.customer_email}
            onChange={handleInputChange('customer_email')}
            InputProps={{
              readOnly: Boolean(customerEmail),
            }}
            sx={{
              '& .MuiInputBase-input': {
                backgroundColor: customerEmail ? 'rgba(0, 0, 0, 0.04)' : 'transparent'
              }
            }}
          />

          <TextField
            fullWidth
            label="Customer Phone"
            value={formData.customer_phone}
            onChange={handleInputChange('customer_phone')}
            InputProps={{
              readOnly: Boolean(customerPhone),
            }}
            sx={{
              '& .MuiInputBase-input': {
                backgroundColor: customerPhone ? 'rgba(0, 0, 0, 0.04)' : 'transparent'
              }
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Batal
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !formData.payment_method}
          startIcon={loading ? <CircularProgress size={20} /> : <PaymentIcon />}
        >
          {loading ? 'Membuat Pembayaran...' : 'Buat Pembayaran'}
        </Button>
      </DialogActions>
    </>
  );

  const renderResultStep = () => (
    <>
      <DialogContent>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <PaymentIcon sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Pembayaran Berhasil Dibuat
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Reference: {createdPayment?.reference}
          </Typography>
        </Box>

        {createdPayment && (
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1">
                  Detail Pembayaran
                </Typography>
                <Chip
                  label={createdPayment.status}
                  color={createdPayment.status === 'UNPAID' ? 'warning' : 'success'}
                  size="small"
                />
              </Box>

              <Typography variant="body2" gutterBottom>
                <strong>Tagihan:</strong> {billNumber}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Jumlah:</strong> {formatCurrency(createdPayment.amount)}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Metode:</strong> {createdPayment.payment_method_name}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Berlaku hingga:</strong> {new Date(createdPayment.expired_time).toLocaleString('id-ID')}
              </Typography>

              {createdPayment.checkout_url && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" gutterBottom>
                    <strong>Pilihan Pembayaran:</strong>
                  </Typography>
                  <Stack direction="column" spacing={1}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<LaunchIcon />}
                      onClick={() => window.open(createdPayment.checkout_url, '_blank')}
                      fullWidth
                    >
                      Bayar via Tripay (Direct)
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<LaunchIcon />}
                      onClick={() => window.open(`${window.location.origin}/payment/${createdPayment.reference}`, '_blank')}
                      fullWidth
                      color="secondary"
                    >
                      Link Pembayaran untuk Customer
                    </Button>
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                        Link untuk Customer: <strong>{`${window.location.origin}/payment/${createdPayment.reference}`}</strong>
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                        <Button
                          size="small"
                          variant="text"
                          startIcon={<CopyIcon />}
                          onClick={handleCopyPaymentUrl}
                        >
                          Copy Tripay URL
                        </Button>
                        <Button
                          size="small"
                          variant="text"
                          startIcon={<CopyIcon />}
                          onClick={() => {
                            const publicUrl = `${window.location.origin}/payment/${createdPayment.reference}`;
                            navigator.clipboard.writeText(publicUrl);
                          }}
                          color="secondary"
                        >
                          Copy Customer URL
                        </Button>
                        {'share' in navigator && (
                          <Button
                            size="small"
                            variant="text"
                            startIcon={<ShareIcon />}
                            onClick={handleSharePayment}
                          >
                            Share
                          </Button>
                        )}
                      </Stack>
                    </Box>
                  </Stack>
                </Box>
              )}
            </CardContent>
          </Card>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleReset} variant="outlined">
          Buat Pembayaran Baru
        </Button>
        <Button onClick={onClose} variant="contained">
          Tutup
        </Button>
      </DialogActions>
    </>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { minHeight: '400px' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            {step === 'form' ? 'Buat Pembayaran' : 'Pembayaran Dibuat'}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      {step === 'form' ? renderFormStep() : renderResultStep()}
    </Dialog>
  );
};

export default PaymentDialog;